import {
	Breakpoints,
	Container,
	ContainerPadding,
	Layout,
	styled,
	Text,
	TextAlignment,
	TokenTextAppearance,
	TokenTextColor,
	Image,
	ContainerGutter,
	CTA,
} from '@volkswagen-onehub/components-core';
import React, { useEffect } from 'react';
import { BehaviorSubject } from 'rxjs';
import { PortalContext } from '../../../../context/PortalContext';
import { getFormValues } from '../../../../helpers/form-helper';
import { Form, GetPersonUser } from '@esportal/portal-service';
import { DialogCompleteRegister } from './dialog-complete-register';
import { getParamsClubVW } from '../../../../helpers/get-params-clubvw';
import { StyledSpinner } from '../../../styled-spinner/styled-spinner';
import { useTrackingManager } from '../../../../tracking/useTrackingManager';
import { pageInPath } from '../../../../tracking/trackingHelper';
import { useErrorAlert } from '../../../../hooks/useErrorAlert';

const StyledContainer = styled.div`
	width: 100%;
`;

export const UserStatusKO: React.FC = () => {
	const portalContext = React.useContext(PortalContext);
	const { showAlert } = useErrorAlert();
	const { portalService } = React.useContext(PortalContext);
	const { device, url, tc } = getParamsClubVW();

	const trackingManager = useTrackingManager();

	useEffect(() => {
		pageInPath('sign-up.app');
		trackingManager.trackClubVWGenericFeatureAppLoad({ contentId: 'sign-up' }, {}, { vehicle: [] });
		trackingManager.trackClubVWFormLoad(
			{
				FormName: 'vw-club-sign_up',
				FormStart: true,
				FormTypeMultiple: ['registration'],
			},
			{ contentId: 'vw-club-sign_up' },
			{ vehicle: [] },
		);
	}, []);

	const [isLoading, setIsLoading] = React.useState<boolean>(false);
	const [dialogState$] = React.useState(new BehaviorSubject<Form | null>(null));

	const submitRegister = (state: Partial<Form>) => {
		if (state?.isValid) {
			const formValues = getFormValues(state as Form);

			setIsLoading(true);
			const request$ = portalService.createPerson(formValues, 'NO INFORMADO', device, tc, url);

			request$.subscribe({
				next: (data: GetPersonUser | null) => {
					setIsLoading(false);
					if (data != null) {
						trackingManager.trackClubVWFormSubmissionSuccessMessageLoad(
							{
								FormName: 'vw-club-sign_up',
								FormStart: false,
								FormTypeMultiple: ['registration'],
							},
							{ contentId: 'vw-club-sign_up' },
							{ vehicle: [] },
						);
					} else {
						showAlert();
					}
				},
				error: () => {
					setIsLoading(false);
					showAlert();
				},
			});
		}
	};

	return (
		<>
			{isLoading ? (
				<StyledSpinner variant="x-large" />
			) : (
				<Container
					padding={{
						top: ContainerPadding.dynamic0200,
						bottom: ContainerPadding.dynamic0200,
					}}
					stretchContent
				>
					<Layout
						data-testid="club-user-ko__layout"
						appearance={{
							[Breakpoints.default]: [
								{ name: '.', columns: 1 },
								{ name: 'a', columns: 22 },
								{ name: '.', columns: 1 },
								{ name: '.', columns: 0 },
								{ name: 'b', columns: 24 },
								{ name: '.', columns: 0 },
								{ name: '.', columns: 1 },
								{ name: 'c', columns: 22 },
								{ name: '.', columns: 1 },
							],
							[Breakpoints.b1280]: [
								{ name: '.', columns: 3 },
								{ name: 'a', columns: 18 },
								{ name: '.', columns: 3 },
								{ name: '.', columns: 0 },
								{ name: 'b', columns: 24 },
								{ name: '.', columns: 0 },
								{ name: '.', columns: 3 },
								{ name: 'c', columns: 18 },
								{ name: '.', columns: 3 },
							],
							[Breakpoints.b1600]: [
								{ name: '.', columns: 5 },
								{ name: 'a', columns: 14 },
								{ name: '.', columns: 5 },
								{ name: '.', columns: 0 },
								{ name: 'b', columns: 24 },
								{ name: '.', columns: 0 },
								{ name: '.', columns: 5 },
								{ name: 'c', columns: 14 },
								{ name: '.', columns: 5 },
							],
							[Breakpoints.b1920]: [
								{ name: '.', columns: 6 },
								{ name: 'a', columns: 12 },
								{ name: '.', columns: 6 },
								{ name: '.', columns: 0 },
								{ name: 'b', columns: 24 },
								{ name: '.', columns: 0 },
								{ name: '.', columns: 6 },
								{ name: 'c', columns: 12 },
								{ name: '.', columns: 6 },
							],
							[Breakpoints.b2560]: [
								{ name: '.', columns: 8 },
								{ name: 'a', columns: 8 },
								{ name: '.', columns: 8 },
								{ name: '.', columns: 0 },
								{ name: 'b', columns: 24 },
								{ name: '.', columns: 0 },
								{ name: '.', columns: 7 },
								{ name: 'c', columns: 10 },
								{ name: '.', columns: 7 },
							],
						}}
						allowOverflowingContent
					>
						<Container wrap={'always'} horizontalAlign={'center'} verticalAlign={'center'} stretchContent>
							<Container padding={{ bottom: ContainerPadding.dynamic0130 }} horizontalAlign={'center'}>
								<Image
									src="https://vgedonehubappsstaweupro.blob.core.windows.net/storage/images-club/logo_club.svg"
									alt="logo club VW"
								/>
							</Container>
							<Text
								appearance={TokenTextAppearance.headline400}
								textAlign={TextAlignment.center}
								color={TokenTextColor.primary}
							>
								Completa tu perfil para unirte al club:
							</Text>
						</Container>
						<Container
							data-testid="user-status-ko-dialog-container"
							horizontalAlign={'center'}
							verticalAlign={'center'}
							stretchContent
						>
							<StyledContainer>
								<PortalContext.Provider value={portalContext}>
									<DialogCompleteRegister state$={dialogState$} submit={submitRegister} />
								</PortalContext.Provider>
							</StyledContainer>
						</Container>
						<Container
							wrap={'always'}
							gutter={ContainerGutter.static250}
							horizontalAlign={'center'}
							padding={{ top: ContainerPadding.dynamic0250 }}
							stretchContent
						>
							<Text
								appearance={TokenTextAppearance.label150}
								color={TokenTextColor.secondary}
								textAlign={TextAlignment.center}
							>
								El responsable del tratamiento es Volkswagen Group España Distribución. Con el registro al Club
								Volkswagen recibirás comunicaciones y encuestas asociadas al Club Volkswagen sobre nuestros productos y
								servicios, promociones y últimas novedades. Si lo deseas, puedes desactivar el envío de estas
								comunicaciones en el apartado “Ajustes de mi membresía del Club Volkswagen”. Puedes ejercer tus derechos
								según se indica en la{' '}
								<CTA
									tag="a"
									emphasis="tertiary"
									href="https://www.volkswagen.es/es/volkswagen-espana/politica-privacidad.html"
									target="_blank"
									onClick={() => {
										trackingManager.trackClubVWLinkClick(
											'Política de privacidad',
											{
												contentId: 'Navigate to privacy policy',
											},
											{ vehicle: [] },
										);
									}}
								>
									<Text
										appearance={TokenTextAppearance.label150}
										color={TokenTextColor.secondary}
										textAlign={TextAlignment.center}
									>
										Política de Privacidad
									</Text>
								</CTA>
								.
							</Text>
						</Container>
					</Layout>
				</Container>
			)}
		</>
	);
};
